import { useField } from 'formik';
import _ from 'lodash';
import { mix } from 'polished';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import CheckmarkIcon from 'packages/kakapo-ui-kit/assets/icons/CheckmarkIcon';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import GoalListItemCompetency from 'packages/kakapo-ui-kit/components/goals/list/GoalListItemCompetency';

import GoalCard from 'goals/GoalCard';
import EvaluationSubmissionAnswer from './EvaluationSubmissionAnswer';

const Container = styled(Box)`
	background: white;
	border-radius: 4px;
`;

const Awaiting = styled(Box)`
	background: ${(props) => mix(0.75, 'white', props.theme.colors.employee)};
	border-radius: 5px;
`;

const ChecklistTaskCheckboxLabel = styled.label<{ disabled?: boolean }>`
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	border: 1px solid ${({ theme }) => theme.colors.lightBlue};
	border-radius: 4px;
	transition:
		100ms border ease-in,
		100ms background-color ease-in;

	input:checked + & {
		border: 1px solid ${({ theme }) => theme.colors.primary};
		background-color: ${({ theme }) => theme.colors.primary};
	}

	${(props) =>
		props.disabled &&
		`
		cursor: default;

		input:checked + & {
			border: 1px solid ${props.theme.colors.slateBlue};
			background-color: ${props.theme.colors.slateBlue};
		}
	`}
`;

function Checkbox(props: {
	name: string;
	disabled?: boolean;
	checked: boolean;
	onClick: () => void;
}) {
	return (
		<Box flexShrink={0}>
			<input
				type="checkbox"
				id={`${props.name}.enabled`}
				checked={props.checked}
				onClick={props.onClick}
				disabled={props.disabled}
				style={{ display: 'none' }}
			/>
			<ChecklistTaskCheckboxLabel htmlFor={`${props.name}.enabled`} disabled={props.disabled}>
				<CheckmarkIcon />
			</ChecklistTaskCheckboxLabel>
		</Box>
	);
}

function EvaluationSubmissionReviewGoal(props: {
	sectionIndex: number;
	itemIndex: number;
	subitemIndex?: number;
	submission: graphql.EvaluationSubmission;
	fillInBy: { manager: boolean; employee: boolean };
	onSave: () => void;
	isSaving: boolean;
}) {
	const { localize } = useLocalize();

	const name = `sections[${props.sectionIndex}].items[${props.itemIndex}]`;

	const [{ value }, , { setValue }] = useField<{
		type: graphql.EvaluationSectionItemType.ReviewGoals;
		reviewGoals?: graphql.EvaluationSubmissionItemReviewGoalInput[];
	}>(name);

	const goals = _.sortBy(props.submission.goals!, [
		(goal) => goal.status,
		(goal) => localize(goal.title).text.toLowerCase(),
	]);

	const goal = goals[props.subitemIndex!];

	const viewerCanUpdate =
		props.fillInBy[
			props.submission.viewer.party === graphql.EvaluationSubmissionParty.Employee
				? 'employee'
				: 'manager'
		];

	const hasEmployeeReviewGoals =
		props.submission.employee.sections[props.sectionIndex].items[
			props.itemIndex
		]?.reviewGoals?.some(({ goalID }) => goalID === goal.goal.id) ?? false;

	const hasManagerReviewGoals =
		props.submission.manager.sections[props.sectionIndex].items[props.itemIndex]?.reviewGoals?.some(
			({ goalID }) => goalID === goal.goal.id,
		) ?? false;

	const index = value.reviewGoals?.findIndex(({ goalID }) => goalID === goal.goal.id) ?? -1;
	const enabled = index >= 0;

	return (
		<>
			<Container p={3} mb={2}>
				<Flex alignItems="center">
					<Box flexGrow={0} flexShrink={0} mr={3}>
						<Checkbox
							name={`${name}.${props.subitemIndex}`}
							checked={enabled}
							disabled={!viewerCanUpdate}
							onClick={() => {
								if (!viewerCanUpdate) {
									return;
								}

								if (enabled) {
									setValue({
										...value,
										reviewGoals: value.reviewGoals?.filter(({ goalID }) => goalID !== goal.goal.id),
									});
								} else {
									setValue({
										...value,
										reviewGoals: [
											...(value.reviewGoals ?? []),
											{
												goalID: goal.goal.id,
												competency: undefined,
												text: { ops: [] },
											},
										],
									});
								}

								props.onSave();
							}}
						/>
					</Box>
					<Box flexGrow={1}>
						<GoalCard
							goal={{
								...goal.goal,
								title: goal.title,
								dueDate: goal.dueDate,
								progress: goal.progress,
								status: goal.status,
								competency: goal.competency,
							}}
							modal
						/>
					</Box>
				</Flex>
				{goal.competency && (
					<Flex mt={2}>
						<Box width={26} mr={3} flexGrow={0} flexShrink={0} />
						<Box flexGrow={1} flexShrink={1}>
							<GoalListItemCompetency competency={goal.competency} />
						</Box>
					</Flex>
				)}
			</Container>
			{(enabled || hasEmployeeReviewGoals || hasManagerReviewGoals) && (
				<Box mb={4}>
					{((enabled &&
						props.submission.viewer.party === graphql.EvaluationSubmissionParty.Employee) ||
						(hasEmployeeReviewGoals &&
							props.submission.viewer.party !== graphql.EvaluationSubmissionParty.Employee)) && (
						<EvaluationSubmissionAnswer
							submission={props.submission}
							party={graphql.EvaluationSubmissionParty.Employee}
							itemIndex={props.itemIndex}
							goal={goal}
							sectionIndex={props.sectionIndex}
							onSave={props.onSave}
							isSaving={props.isSaving}
						/>
					)}

					{((enabled &&
						props.submission.viewer.party === graphql.EvaluationSubmissionParty.Manager) ||
						(hasEmployeeReviewGoals &&
							props.submission.viewer.party !== graphql.EvaluationSubmissionParty.Manager)) && (
						<EvaluationSubmissionAnswer
							submission={props.submission}
							party={graphql.EvaluationSubmissionParty.Manager}
							itemIndex={props.itemIndex}
							goal={goal}
							sectionIndex={props.sectionIndex}
							onSave={props.onSave}
							isSaving={props.isSaving}
						/>
					)}
				</Box>
			)}
		</>
	);
}

export default function EvaluationSubmissionReviewGoals(props: {
	section: number;
	item: number;
	submission: graphql.EvaluationSubmission;
	fillInBy: { manager: boolean; employee: boolean };
	onSave: () => void;
	isSaving: boolean;
}) {
	const { t } = useTranslation();

	const activeGoals = props.submission.goals?.filter(
		(goal) => goal.status === graphql.GoalStatus.Active,
	);
	const completedGoals = props.submission.goals?.filter(
		(goal) => goal.status === graphql.GoalStatus.Completed,
	);

	const viewerCanUpdate =
		props.fillInBy[
			props.submission.viewer.party === graphql.EvaluationSubmissionParty.Employee
				? 'employee'
				: 'manager'
		];

	return (
		<>
			<Box mb={2}>
				<SubSectionTitle color="black">
					{t('evaluation.submission.reviewGoals.activeTitle')}
				</SubSectionTitle>
			</Box>
			{!viewerCanUpdate && (
				<Awaiting p={3} mb={2}>
					{t(
						`evaluation.submission.irrelevant.${
							props.submission.viewer.party === graphql.EvaluationSubmissionParty.Employee
								? 'manager'
								: 'employee'
						}`,
					)}
				</Awaiting>
			)}
			{activeGoals?.map((_, index) => (
				<EvaluationSubmissionReviewGoal
					key={index}
					sectionIndex={props.section}
					itemIndex={props.item}
					subitemIndex={index}
					submission={props.submission}
					fillInBy={props.fillInBy}
					onSave={props.onSave}
					isSaving={props.isSaving}
				/>
			))}
			{activeGoals?.length === 0 && (
				<ParagraphSmall color="slateBlue">
					<Box textAlign="center">{t('evaluation.submission.reviewGoals.activeEmpty')}</Box>
				</ParagraphSmall>
			)}
			<Box mb={2} mt={3}>
				<SubSectionTitle color="black">
					{t('evaluation.submission.reviewGoals.completedTitle')}
				</SubSectionTitle>
			</Box>
			{completedGoals?.map((_, index) => (
				<EvaluationSubmissionReviewGoal
					key={index}
					sectionIndex={props.section}
					itemIndex={props.item}
					subitemIndex={activeGoals!.length + index}
					submission={props.submission}
					fillInBy={props.fillInBy}
					onSave={props.onSave}
					isSaving={props.isSaving}
				/>
			))}
			{completedGoals?.length === 0 && (
				<ParagraphSmall color="slateBlue">
					<Box textAlign="center">{t('evaluation.submission.reviewGoals.completedEmpty')}</Box>
				</ParagraphSmall>
			)}
		</>
	);
}
